/**
 * Type Compatibility Matrix
 *
 * Defines which Salesforce field types are compatible with application field types
 * for ERP field binding.
 */

// Application field types (bindable types only)
export type AppFieldType =
    | 'str'
    | 'int'
    | 'float'
    | 'datetime'
    | 'bool'
    | 'time_serie_timing'; // for start_date and end_date fields

// Salesforce field types (based on Salesforce API)
export type SalesforceFieldType =
    | 'string' | 'textarea' | 'phone' | 'email' | 'url'
    | 'picklist' | 'multipicklist'
    | 'reference' | 'id'
    | 'combobox' | 'encryptedstring' | 'anyType' | 'base64'
    | 'address' | 'location'
    | 'boolean'
    | 'date' | 'datetime' | 'time'
    | 'int' | 'long' | 'double' | 'currency' | 'percent';

/**
 * Compatibility matrix mapping app field types to compatible Salesforce field types
 *
 * General philosophy:
 * - String/Text types are very permissive (can represent almost anything)
 * - Numeric types accept other numeric types and can parse strings
 * - Date types accept date/datetime and can parse strings
 * - Boolean is more strict but can parse strings
 */
export const TYPE_COMPATIBILITY_MATRIX: Record<AppFieldType, SalesforceFieldType[]> = {
    // String type - compatible with almost everything (strings can represent any value)
    'str': [
        // All text types
        'string', 'textarea', 'phone', 'email', 'url',
        'picklist', 'multipicklist',
        'reference', 'id',
        'combobox', 'encryptedstring', 'anyType', 'base64',
        'address', 'location',
        // Numeric types (can be converted to string representation)
        'int', 'long', 'double', 'currency', 'percent',
        // Boolean (can be "true"/"false" strings)
        'boolean',
        // Date/time (can be formatted as string)
        'date', 'datetime', 'time'
    ],

    // Integer type - accept integer and numeric types only
    'int': [
        'int', 'long', // Native integer types
        'double', 'currency', 'percent' // Can be rounded
    ],

    // Float type - accept float and numeric types only
    'float': [
        'double', 'currency', 'percent', // Native float types
        'int', 'long' // Can be converted to float
    ],

    // DateTime type - accept date/datetime and parseable strings
    'datetime': [
        'datetime', 'date', 'time', // Native datetime types
        'string', 'textarea' // Can parse datetime strings
    ],

    // Boolean type - accept boolean and string representations
    'bool': [
        'boolean', // Native boolean
        'string', 'picklist' // Can parse "true"/"false", "yes"/"no", "1"/"0"
    ],

    // Time series timing - date fields for start_date and end_date
    'time_serie_timing': [
        'date', 'datetime', // Native date types
        'string', 'textarea' // Can parse date strings
    ],
};

/**
 * Check if a Salesforce field type is compatible with an application field type
 *
 * @param appFieldType - The application field type
 * @param salesforceFieldType - The Salesforce field type to check
 * @returns true if compatible, false otherwise
 *
 * @example
 * isTypeCompatible('datetime', 'date') // returns true
 * isTypeCompatible('int', 'string') // returns true (strings can be parsed as numbers)
 */
export function isTypeCompatible(appFieldType: string, salesforceFieldType: string): boolean {
    // Normalize to lowercase for case-insensitive comparison
    const normalizedAppFieldType = appFieldType.toLowerCase();
    const normalizedSalesforceFieldType = salesforceFieldType.toLowerCase();

    const compatibleTypes = TYPE_COMPATIBILITY_MATRIX[normalizedAppFieldType as AppFieldType];

    if (!compatibleTypes) {
        // If app field type is not in the matrix, be conservative and return false
        console.warn(`Unknown app field type: ${appFieldType}`);
        return false;
    }

    return compatibleTypes.includes(normalizedSalesforceFieldType as SalesforceFieldType);
}

/**
 * Get all compatible Salesforce field types for a given app field type
 *
 * @param appFieldType - The application field type
 * @returns Array of compatible Salesforce field types
 */
export function getCompatibleSalesforceTypes(appFieldType: string): SalesforceFieldType[] {
    // Normalize to lowercase for case-insensitive comparison
    const normalizedAppFieldType = appFieldType.toLowerCase();
    return TYPE_COMPATIBILITY_MATRIX[normalizedAppFieldType as AppFieldType] || [];
}

/**
 * Get a human-readable description of why types are incompatible
 *
 * @param appFieldType - The application field type
 * @param salesforceFieldType - The Salesforce field type
 * @returns A description string, or null if types are compatible
 */
export function getIncompatibilityReason(appFieldType: string, salesforceFieldType: string): string | null {
    if (isTypeCompatible(appFieldType, salesforceFieldType)) {
        return null;
    }

    // Normalize to lowercase for case-insensitive comparison
    const normalizedAppFieldType = appFieldType.toLowerCase();
    const compatibleTypes = TYPE_COMPATIBILITY_MATRIX[normalizedAppFieldType as AppFieldType];

    if (!compatibleTypes) {
        return `Unknown application field type: ${appFieldType}`;
    }

    // Provide specific reasons for common mismatches (using normalized type for comparison)
    if (normalizedAppFieldType === 'bool') {
        return `Boolean fields require Salesforce boolean, string, or picklist fields (received: ${salesforceFieldType})`;
    }

    if (['datetime', 'time_serie_timing'].includes(normalizedAppFieldType)) {
        return `Date/time fields require Salesforce date, datetime, time, or text fields that can be parsed as dates (received: ${salesforceFieldType})`;
    }

    if (['int', 'float'].includes(normalizedAppFieldType)) {
        return `Numeric fields require Salesforce numeric fields (int, long, double, currency, percent) (received: ${salesforceFieldType})`;
    }

    if (normalizedAppFieldType === 'str') {
        // String is compatible with almost everything, so if we're here it's unusual
        return `Text field cannot accept ${salesforceFieldType} type`;
    }

    return `Type mismatch: ${appFieldType} is not compatible with ${salesforceFieldType}`;
}
